/**
 * 打印时非空单元格检测工具
 * 优化了原有的简单判定逻辑，支持合并单元格、样式、边框等复杂情况
 */

import Store from "../store";
import { getObjType } from "./util";
import { isRealNull } from "../global/validate";
import { isInlineStringCell } from "../controllers/inlineString";
import { getRealCellValue } from "../global/getdata";
import menuButton from "../controllers/menuButton";
import alternateformat from "../controllers/alternateformat";
import conditionformat from "../controllers/conditionformat";
import { getBorderInfoComputeRange } from "../global/border";

/**
 * 检测单元格是否为非空单元格（用于打印范围计算）
 * @param {number} r 行索引
 * @param {number} c 列索引
 * @param {Object} options 可选参数
 * @param {boolean} options.includeMergedCells 是否包含合并单元格（默认true）
 * @param {boolean} options.includeStyledCells 是否包含有样式的单元格（默认true）
 * @param {boolean} options.includeBorderedCells 是否包含有边框的单元格（默认true）
 * @param {boolean} options.includeConditionalFormatting 是否包含条件格式单元格（默认true）
 * @param {boolean} options.includeAlternateFormatting 是否包含交替颜色单元格（默认true）
 * @returns {boolean} 是否为非空单元格
 */
function isNonEmptyCellForPrint(r, c, options = {}) {
	const {
		includeMergedCells = true,
		includeStyledCells = true,
		includeBorderedCells = true,
		includeConditionalFormatting = true,
		includeAlternateFormatting = true,
	} = options;

	// 检查行列是否被隐藏
	if (Store.config["rowhidden"] != null && Store.config["rowhidden"][r] != null) {
		return false;
	}
	if (Store.config["colhidden"] != null && Store.config["colhidden"][c] != null) {
		return false;
	}

	const cell = Store.flowdata[r] && Store.flowdata[r][c];

	// 1. 基础内容检查
	if (cell != null && !isRealNull(cell.v)) {
		return true;
	}

	// 2. 内联字符串单元格检查
	if (cell != null && isInlineStringCell(cell)) {
		return true;
	}

	// 3. 合并单元格检查
	if (includeMergedCells && cell != null && getObjType(cell) == "object" && "mc" in cell) {
		// 如果是合并单元格的主单元格（包含rs和cs属性）
		if ("rs" in cell.mc) {
			return true;
		}
		// 如果是合并单元格的子单元格，检查主单元格是否有内容
		else if (cell.mc.r != null && cell.mc.c != null) {
			const mainCell = Store.flowdata[cell.mc.r] && Store.flowdata[cell.mc.r][cell.mc.c];
			if (mainCell && !isRealNull(getRealCellValue(cell.mc.r, cell.mc.c))) {
				return true;
			}
		}
	}

	// 4. 样式检查（背景色、字体颜色等）
	if (includeStyledCells && cell != null) {
		// 检查背景色
		const bgColor = menuButton.checkstatus(Store.flowdata, r, c, "bg");
		if (bgColor != null && bgColor !== "#FFFFFF") {
			return true;
		}

		// 检查字体颜色（非默认黑色）
		const fontColor = menuButton.checkstatus(Store.flowdata, r, c, "fc");
		if (fontColor != null && fontColor !== "#000000") {
			return true;
		}

		// 检查字体样式（粗体、斜体等）
		const bold = menuButton.checkstatus(Store.flowdata, r, c, "bl");
		const italic = menuButton.checkstatus(Store.flowdata, r, c, "it");
		if (bold == "1" || italic == "1") {
			return true;
		}

		// 检查字体大小（非默认10px）
		const fontSize = menuButton.checkstatus(Store.flowdata, r, c, "fs");
		if (fontSize != null && fontSize !== "10") {
			return true;
		}

		// 检查字体家族（非默认）
		const fontFamily = menuButton.checkstatus(Store.flowdata, r, c, "ff");
		if (fontFamily != null && fontFamily !== "0") {
			return true;
		}

		// 检查标题色
		if (cell.tc != null) {
			return true;
		}
	}

	// 5. 边框检查
	if (includeBorderedCells) {
		const borderInfo = getBorderInfoComputeRange(r, r, c, c);
		if (borderInfo == null) {
			return false;
		}
		const cellKey = r + "_" + c;
		if (borderInfo[cellKey]) {
			const borders = borderInfo[cellKey];
			if (borders.l || borders.r || borders.t || borders.b) {
				return true;
			}
		}
	}

	// 6. 条件格式检查
	if (includeConditionalFormatting) {
		try {
			const cf_compute = conditionformat.getComputeMap();
			const checksCF = conditionformat.checksCF(r, c, cf_compute);
			if (checksCF != null) {
				// 检查条件格式背景色
				if (checksCF["cellColor"] != null) {
					return true;
				}
				// 检查条件格式文本颜色
				if (checksCF["textColor"] != null) {
					return true;
				}
				// 检查数据条
				if (checksCF["dataBar"] != null) {
					return true;
				}
				// 检查图标集
				if (checksCF["icons"] != null) {
					return true;
				}
			}
		} catch (error) {
			console.warn("条件格式检查失败:", error);
		}
	}

	// 7. 交替颜色检查
	if (includeAlternateFormatting) {
		try {
			const af_compute = alternateformat.getComputeMap();
			const checksAF = alternateformat.checksAF(r, c, af_compute);
			if (checksAF != null && (checksAF[0] != null || checksAF[1] != null)) {
				return true;
			}
		} catch (error) {
			console.warn("交替颜色检查失败:", error);
		}
	}

	return false;
}

/**
 * 获取合并单元格的实际边界
 * @param {number} r 行索引
 * @param {number} c 列索引
 * @returns {Object} 合并单元格边界信息 {startRow, endRow, startCol, endCol}
 */
function getMergedCellBounds(r, c) {
	const cell = Store.flowdata[r] && Store.flowdata[r][c];

	if (cell != null && getObjType(cell) == "object" && "mc" in cell) {
		if ("rs" in cell.mc) {
			// 主单元格
			return {
				startRow: cell.mc.r,
				endRow: cell.mc.r + cell.mc.rs - 1,
				startCol: cell.mc.c,
				endCol: cell.mc.c + cell.mc.cs - 1,
			};
		} else {
			// 子单元格，返回主单元格的边界
			const mainR = cell.mc.r;
			const mainC = cell.mc.c;
			const mainCell = Store.flowdata[mainR] && Store.flowdata[mainR][mainC];

			if (mainCell && mainCell.mc && "rs" in mainCell.mc) {
				return {
					startRow: mainCell.mc.r,
					endRow: mainCell.mc.r + mainCell.mc.rs - 1,
					startCol: mainCell.mc.c,
					endCol: mainCell.mc.c + mainCell.mc.cs - 1,
				};
			}
		}
	}

	return {
		startRow: r,
		endRow: r,
		startCol: c,
		endCol: c,
	};
}

/**
 * 获取打印范围内的非空单元格行列索引
 * @param {Object} options 可选参数
 * @param {number} options.startRow 开始行（可选，默认为0）
 * @param {number} options.endRow 结束行（可选，默认为所有行）
 * @param {number} options.startCol 开始列（可选，默认为0）
 * @param {number} options.endCol 结束列（可选，默认为所有列）
 * @param {Object} options.cellOptions 单元格检测选项
 * @returns {Object} 包含非空行和列的数组 {nonEmptyRows: [], nonEmptyCols: []}
 */
function getNonEmptyCellsForPrint(options = {}) {
	const {
		startRow = 0,
		endRow = Store.visibledatarow.length - 1,
		startCol = 0,
		endCol = Store.visibledatacolumn.length - 1,
		cellOptions = {},
	} = options;

	const nonEmptyRows = [];
	const nonEmptyCols = [];
	const processedMergedCells = new Set(); // 避免重复处理合并单元格

	for (let r = startRow; r <= endRow; r++) {
		for (let c = startCol; c <= endCol; c++) {
			if (isNonEmptyCellForPrint(r, c, cellOptions)) {
				// 处理合并单元格
				const bounds = getMergedCellBounds(r, c);
				const cellKey = `${bounds.startRow}_${bounds.startCol}`;

				// 如果是合并单元格的主单元格且未被处理过
				if (!processedMergedCells.has(cellKey)) {
					processedMergedCells.add(cellKey);

					// 添加合并单元格的所有行列
					for (let mr = bounds.startRow; mr <= bounds.endRow; mr++) {
						if (!nonEmptyRows.includes(mr)) {
							nonEmptyRows.push(mr);
						}
					}
					for (let mc = bounds.startCol; mc <= bounds.endCol; mc++) {
						if (!nonEmptyCols.includes(mc)) {
							nonEmptyCols.push(mc);
						}
					}
				}
			}
		}
	}

	// 排序
	nonEmptyRows.sort((a, b) => a - b);
	nonEmptyCols.sort((a, b) => a - b);

	return {
		nonEmptyRows,
		nonEmptyCols,
	};
}

/**
 * 优化的打印范围计算函数
 * 替换原有的简单判定逻辑
 * @param {Object} options 可选参数
 * @returns {Object} 包含非空行列的数组
 */
function calculatePrintRange(options = {}) {
	const defaultOptions = {
		includeMergedCells: true,
		includeStyledCells: true,
		includeBorderedCells: true,
		includeConditionalFormatting: true,
		includeAlternateFormatting: true,
	};

	const cellOptions = { ...defaultOptions, ...options };

	return getNonEmptyCellsForPrint({ cellOptions });
}

export { isNonEmptyCellForPrint, getMergedCellBounds, getNonEmptyCellsForPrint, calculatePrintRange };
